import { useNavigation } from "@react-navigation/native";
import { Box, Button, Text, useTheme, VStack } from "native-base";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import PostOnboardingLoadingInterstitial from "../../components/PostOnboardingLoadingInterstitial";
import Routes from "../../constants/routeNames";
import {
  clearError,
  selectError,
  selectIsCompleted,
  selectIsSubmitting,
  submitSimpleOnboarding,
} from "../../slices/simpleOnboardingSlice";

const PostOnboardingLoadingScreen = (): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigation = useNavigation();
  const dispatch = useDispatch();

  const isSubmitting = useSelector(selectIsSubmitting);
  const error = useSelector(selectError);
  const isCompleted = useSelector(selectIsCompleted);

  // Add artificial loading delay state
  const [loadingStartTime] = useState(Date.now());
  const [minimumLoadingTime] = useState(
    () =>
      // Random between 3000-4000ms (3-4 seconds)
      3000 + Math.floor(Math.random() * 1000)
  );

  useEffect(() => {
    // Submit onboarding data when component mounts
    dispatch(submitSimpleOnboarding());
  }, [dispatch]);

  useEffect(() => {
    // Navigate to diary when submission is complete
    if (isCompleted && !error) {
      // Continue with existing navigation logic
      const elapsedTime = Date.now() - loadingStartTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);

      // Wait for remaining time plus 1 second transition
      const timer = setTimeout(() => {
        navigation.reset({
          index: 0,
          routes: [{ name: Routes.PostOnboardingScreen as never }],
        });
      }, remainingTime + 1000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isCompleted, error, navigation, loadingStartTime, minimumLoadingTime]);

  // Show error state if submission fails
  if (error && !isSubmitting) {
    return (
      <Box flex={1} bg="function.10" justifyContent="center" alignItems="center" safeArea>
        <VStack space={4} alignItems="center" px={6}>
          <Text fontSize="xl" fontWeight="bold" fontFamily={theme.fonts.heading} textAlign="center">
            {t("simple_onboarding.loading_screen.error_title")}
          </Text>
          <Text fontSize="md" color="function.30" fontFamily={theme.fonts.body} textAlign="center">
            {error}
          </Text>
          <Button
            bg="primary.50"
            borderRadius={theme.borderRadii.button.default}
            _pressed={{ bg: "primary.50:alpha.80" }}
            onPress={() => {
              dispatch(clearError());
              dispatch(submitSimpleOnboarding());
            }}
            size="lg"
            mt={4}
          >
            <Text color="primary.10" fontWeight="bold" fontFamily={theme.fonts.body}>
              {t("simple_onboarding.loading_screen.retry_button")}
            </Text>
          </Button>
          <Button
            variant="ghost"
            onPress={() => {
              // Allow user to skip and go to welcome tour
              navigation.reset({
                index: 0,
                routes: [{ name: Routes.PostOnboardingScreen as never }],
              });
            }}
            size="sm"
          >
            <Text color="function.30" fontFamily={theme.fonts.body}>
              {t("simple_onboarding.loading_screen.skip_button")}
            </Text>
          </Button>
        </VStack>
      </Box>
    );
  }

  // Show loading state
  return <PostOnboardingLoadingInterstitial size="lg" />;
};

export default PostOnboardingLoadingScreen;
